import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  Input,
  HostListener,
  SecurityContext,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ChatService } from '../../../core/services/chat/chat.service';
import { ChatSignalRService } from '../../../core/services/chat/chat-signalr.service';
import { CurrentUserService } from '../../../core/services/auth/current-user.service';
import { UserService } from '../../../core/services/user/user.service';
import {
  ChatMessage,
  MessageListData,
} from '../../../core/models/chat/message-list-response.model';
import {
  ChatHubMessage,
  ConnectionState,
} from '../../../core/models/chat/chat-hub.model';

import { SendMessageRequest } from '../../../core/models/chat/send-message.model';
import { UploadFileRequest } from '../../../core/models/chat/upload-file.model';
import { environment } from '../../../../environments/environment';
import { CallHubService } from 'app/core/services/chat/call-hub.service';
import { ModalService } from 'app/main/shared/services/modal.service';
import { VideoCallModalComponent } from 'app/shared/components/modal/video-call-modal/video-call-modal.component';
import { PatientsService } from 'app/core/services/patient/patients.service';
import { WoundService } from 'app/core/services/wound/wound.service';
import { CallContact } from 'app/core/models/patient/call-contacts.model';
import { PatientDetailResponse } from 'app/core/models/patient/patient-detail.model';
import { AskToSpecialistModalComponent } from 'app/shared/components/modal/ask-to-specialist-modal/ask-to-specialist-modal.component';
import { PermissionService } from 'app/core/services/user/permission.service';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageContainer') private messageContainer!: ElementRef;
  @ViewChild('fileInput') private fileInput!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;

  @Input() groupId: string = '';
  @Input() assignedDoctorIds?: string[] = [];
  @Input() patientInfo: PatientDetailResponse | null;

  messages: ChatMessage[] = [];

  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  hasMore: boolean = false;

  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  isSending: boolean = false;
  connectionState: ConnectionState = ConnectionState.Disconnected;

  messageText: string = '';
  selectedFile: File | null = null;
  selectedFileName: string = '';

  currentUserId: string = '';
  currentUserName: string = '';
  currentUserAvatar: string | null = null;

  isLightboxOpen: boolean = false;
  lightboxImageUrl: string | null = null;
  
  previewUrl: SafeResourceUrl | null = null;
  previewFileName: string = '';
  previewFilePath: string = '';
  isPreviewLoading: boolean = false;
  previewHasError: boolean = false;
  previewFileType: 'image' | 'pdf' | 'other' = 'other';
  
  isActionsMenuOpen: boolean = false;

  callContacts: CallContact[] = [];
  isLoadingContacts: boolean = false;
  showContactSelector: boolean = false;
  selectedCallType: 'audio' | 'video' | null = null;
  contactSearchTerm: string = '';
  isInCall: boolean = false;

  private shouldScrollToBottom: boolean = true;
  private destroy$ = new Subject<void>();
  private answeringConsultationIds = new Set<string>();
  private readonly consultationActionCompletedStoragePrefix =
    'sore:woundConsultationActionCompleted:';

  ConnectionState = ConnectionState;
  readonly consultationActionType = 2;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chatService: ChatService,
    private signalRService: ChatSignalRService,
    private readonly callHubService: CallHubService,
    private currentUserService: CurrentUserService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private appModalService: ModalService,
    private patientsService: PatientsService,
    private woundService: WoundService,
    private sanitizer: DomSanitizer,
    private permissionService: PermissionService
  ) {}

  get isNurse(): boolean {
    return this.permissionService.hasPermission('nurse_claim');
  }

  get isSpecialist(): boolean {
    return this.permissionService.hasPermission('specialist_claim');
  }

  ngOnInit(): void {
    const session = this.currentUserService.GetSession();

    if (session) {
      this.currentUserId = session.id || '';

      let fullName = '';
      if (session.firstName && session.lastName) {
        fullName = `${session.firstName} ${session.lastName}`;
      } else if (session.firstName) {
        fullName = session.firstName;
      } else if (session.lastName) {
        fullName = session.lastName;
      }

      this.currentUserName =
        fullName ||
        session.name ||
        session.userName ||
        session.fullName ||
        session.email?.split('@')[0] ||
        'User';

      this.loadCurrentUserAvatar();
    }

    if (this.groupId) {
      this.initializeChat();
    } else {
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        this.groupId = params['groupId'];

        if (this.groupId) {
          this.initializeChat();
        }
      });
    }

    this.callHubService
      .getCallState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isInCall = state.inCall;
        this.cdr.detectChanges();
      });
  }

  private loadCurrentUserAvatar(): void {
    this.userService
      .getCurrentUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (
            response &&
            response.isSuccess &&
            response.data &&
            response.data.avatar
          ) {
            if (
              response.data.avatar.startsWith('iVBOR') ||
              response.data.avatar.startsWith('/9j/')
            ) {
              this.currentUserAvatar = `data:image/png;base64,${response.data.avatar}`;
            } else {
              this.currentUserAvatar = response.data.avatar;
            }
          }
        },
        error: (error) => {},
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.signalRService.disconnect();
  }

  private async initializeChat(): Promise<void> {
    try {
      await this.connectToHub();
      await this.loadMessages();
      this.subscribeToNewMessages();
      this.subscribeToConnectionState();
    } catch (error) {
      console.error('Failed to initialize chat', error);
      console.warn('Chat will work without real-time updates');
      await this.loadMessages();
    }
  }

  private async connectToHub(): Promise<void> {
    try {
      await this.signalRService.connect(this.groupId);
    } catch (error) {
      console.warn(
        'SignalR connection failed, continuing without real-time updates'
      );
    }
  }

  private async loadMessages(
    page: number = 1,
    options: { silent?: boolean } = {},
  ): Promise<void> {
    const silent = options.silent === true;

    if (page === 1) {
      if (!silent) {
        this.isLoading = true;
      }
    } else {
      this.isLoadingMore = true;
    }

    try {
      const response = await this.chatService
        .getListMessage(this.groupId, page, this.pageSize)
        .toPromise();

      if (response && response.isSuccess && response.data) {
        const payload = response.data;
        const messages = this.getMessagesFromPayload(payload);

        if (!messages) {
          console.warn('Unexpected chat message response shape', payload);
          return;
        }

        const transformedMessages = messages.map((msg) =>
          this.applyLocalConsultationActionCompletion({
            ...msg,
            chatMessageImages: msg.chatMessageImages?.map((img) => ({
              ...img,
              imagePath: this.getImageUrl(img.imagePath) || img.imagePath,
            })),
          }),
        );

        if (page === 1) {
          this.messages = transformedMessages;
          this.shouldScrollToBottom = true;
        } else {
          this.messages = [...transformedMessages, ...this.messages];
          this.shouldScrollToBottom = false;
        }

        this.currentPage = page;
        this.hasMore = Array.isArray(payload)
          ? messages.length >= this.pageSize
          : Boolean(payload.hasNext ?? messages.length >= this.pageSize);
      }
    } catch (error) {
      console.error('Failed to load messages', error);
      if (!this.messages) {
        this.messages = [];
      }
    } finally {
      if (!silent) {
        this.isLoading = false;
      }
      if (page !== 1) {
        this.isLoadingMore = false;
      }
    }
  }

  private subscribeToNewMessages(): void {
    this.signalRService.newMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (message: ChatHubMessage) => {
        if (this.shouldRefreshConsultationMessages(message)) {
          await this.loadMessages(1, { silent: true });
          return;
        }

        const chatMessage: ChatMessage = {
          id: Date.now().toString(),
          groupId: message.groupId,
          senderId: message.senderId,
          senderName: message.senderName,
          message: message.message || '',
          createdDate: message.sentAt
            ? new Date(message.sentAt).toString()
            : new Date().toString(),
          chatMessageImages: message.imagePath
            ? [
                {
                  id: Date.now().toString(),
                  imagePath:
                    this.getImageUrl(message.imagePath) || message.imagePath,
                },
              ]
            : [],
        };

        this.messages.push(chatMessage);
        this.shouldScrollToBottom = true;
        this.cdr.detectChanges();
      });
  }

  private getMessagesFromPayload(
    payload: MessageListData | ChatMessage[],
  ): ChatMessage[] | null {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && Array.isArray(payload.items)) {
      return payload.items;
    }

    return null;
  }

  private applyLocalConsultationActionCompletion(
    message: ChatMessage,
  ): ChatMessage {
    if (
      !message.woundConsultationId ||
      message.consultationActionCompleted ||
      !this.hasLocalConsultationActionCompletion(message.woundConsultationId)
    ) {
      return message;
    }

    return {
      ...message,
      consultationActionCompleted: true,
      canOpenConsultationAction: false,
    };
  }

  private hasLocalConsultationActionCompletion(
    woundConsultationId: string,
  ): boolean {
    try {
      return (
        typeof localStorage !== 'undefined' &&
        localStorage.getItem(
          `${this.consultationActionCompletedStoragePrefix}${woundConsultationId}`,
        ) !== null
      );
    } catch {
      return false;
    }
  }

  private subscribeToConnectionState(): void {
    this.signalRService.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.connectionState = state;
      });
  }

  async sendMessage(): Promise<void> {
    if (!this.messageText.trim() && !this.selectedFile) {
      return;
    }

    if (!this.signalRService.isConnected()) {
      return;
    }

    this.isSending = true;

    try {
      let imagePath: string | null = null;

      if (this.selectedFile) {
        imagePath = await this.uploadFile();

        if (imagePath && !this.messageText.trim()) {
          this.messageText = '';
          this.clearFileSelection();
          this.isSending = false;
          return;
        }
      }

      if (this.messageText.trim() || imagePath) {
        const hubMessage: ChatHubMessage = {
          groupId: this.groupId,
          senderId: this.currentUserId,
          senderName: this.currentUserName,
          message: this.messageText.trim() || null,
          imagePath: imagePath,
          patientId: this.patientInfo?.id ?? null,
          patientName:
            this.patientInfo !== null && this.patientInfo !== undefined
              ? `${this.patientInfo.name} ${this.patientInfo.surname}`
              : null,
          sentAt: new Date(),
          doctorIds: this.assignedDoctorIds ?? [],
        };

        await this.signalRService.sendMessage(hubMessage);
      }

      this.messageText = '';
      this.clearFileSelection();
      
      setTimeout(() => {
        const textarea = this.messageInput?.nativeElement;
        if (textarea) {
          textarea.style.height = 'auto';
        }
      }, 0);
    } catch (error) {
    } finally {
      this.isSending = false;
      setTimeout(() => {
        this.messageInput?.nativeElement?.focus();
      }, 0);
    }
  }

  private async uploadFile(): Promise<string | null> {
    if (!this.selectedFile) return null;

    try {
      const request: UploadFileRequest = {
        file: this.selectedFile,
        groupId: this.groupId,
        senderId: this.currentUserId,
        senderName: this.currentUserName,
      };

      const response = await this.chatService.uploadFile(request).toPromise();

      if (
        response &&
        response.isSuccess &&
        response.data &&
        response.data.filePath
      ) {
        return response.data.filePath;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.selectedFileName = this.selectedFile.name;
    }
  }

  clearFileSelection(): void {
    this.selectedFile = null;
    this.selectedFileName = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  async loadMoreMessages(): Promise<void> {
    if (this.isLoadingMore || !this.hasMore) {
      return;
    }

    await this.loadMessages(this.currentPage + 1);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    if (element.scrollTop === 0 && this.hasMore && !this.isLoadingMore) {
      this.loadMoreMessages();
    }
  }

  scrollToBottom(): void {
    try {
      if (this.messageContainer) {
        setTimeout(() => {
          if (this.messageContainer) {
            this.messageContainer.nativeElement.scrollTop =
              this.messageContainer.nativeElement.scrollHeight;
          }
        }, 0);
      }
    } catch (err) {
      console.error('Error scrolling to bottom', err);
    }
  }

  isOwnMessage(message: ChatMessage): boolean {
    return message.senderId === this.currentUserId;
  }

  trackMessageById(index: number, message: ChatMessage): string {
    return message.id || `${message.groupId}-${message.createdDate}-${index}`;
  }

  isConsultationActionMessage(message: ChatMessage): boolean {
    return Number(message.chatMessageType) === this.consultationActionType;
  }

  isAnsweringConsultation(message: ChatMessage): boolean {
    return !!message.woundConsultationId &&
      this.answeringConsultationIds.has(message.woundConsultationId);
  }

  showSpecialistConsultationCompleted(message: ChatMessage): boolean {
    return (
      this.isSpecialist &&
      !message.canAnswerConsultation &&
      !message.canViewConsultationAnswer
    );
  }

  answerConsultation(message: ChatMessage, hasWound: boolean): void {
    if (
      !message.woundConsultationId ||
      !message.canAnswerConsultation ||
      this.isAnsweringConsultation(message)
    ) {
      return;
    }

    const consultationId = message.woundConsultationId;
    const wasAnswerable = message.canAnswerConsultation;

    this.answeringConsultationIds.add(consultationId);
    message.canAnswerConsultation = false;
    this.cdr.detectChanges();

    this.woundService
      .answerWoundConsultationHasWound({
        id: consultationId,
        hasWound,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.answeringConsultationIds.delete(consultationId);
        }),
      )
      .subscribe({
        next: () => {},
        error: (error) => {
          message.canAnswerConsultation = wasAnswerable;
          this.cdr.detectChanges();
          console.error('Failed to answer wound consultation', error);
        },
      });
  }

  openConsultationAction(message: ChatMessage): void {
    if (
      !message.woundConsultationId ||
      !message.canOpenConsultationAction ||
      message.consultationHasWound === null ||
      message.consultationHasWound === undefined
    ) {
      return;
    }

    const patientId = message.patientId || this.patientInfo?.id || this.groupId;

    this.router.navigate(['/patient', 'edit', patientId], {
      queryParams: {
        startStep: 3,
        hideStepper: true,
        hasWound: message.consultationHasWound,
        woundConsultationId: message.woundConsultationId,
      },
    });
  }

  getConsultationAnswerLabel(message: ChatMessage): string {
    return message.consultationHasWound
      ? 'woundConsultationDetail.yes'
      : 'woundConsultationDetail.no';
  }

  getConsultationActionLabel(message: ChatMessage): string {
    return message.consultationHasWound ? 'addWound' : 'score';
  }

  private shouldRefreshConsultationMessages(message: ChatHubMessage): boolean {
    return (
      message.requiresRefresh === true ||
      Number(message.chatMessageType) === this.consultationActionType
    );
  }

  formatTime(dateString: string | null): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  }

  getUserAvatar(senderId: string): string | null {
    if (senderId === this.currentUserId) {
      return this.currentUserAvatar;
    }
    return null;
  }

  getInitials(name: string | null): string {
    if (!name) return '?';

    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  get filteredCallContacts(): CallContact[] {
    if (!this.contactSearchTerm.trim()) {
      return this.callContacts;
    }
    
    const searchLower = this.contactSearchTerm.toLowerCase().trim();
    return this.callContacts.filter(contact => 
      contact.fullName.toLowerCase().includes(searchLower)
    );
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    } else {
      setTimeout(() => this.autoResizeTextarea(), 0);
    }
  }

  autoResizeTextarea(): void {
    const textarea = this.messageInput?.nativeElement;
    if (!textarea) return;

    textarea.style.height = 'auto';
    
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 42), 120);
    textarea.style.height = `${newHeight}px`;
  }

  getImageUrl(imagePath: string | null | undefined): string | null {
    if (!imagePath) return null;

    if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('blob:')) {
      return imagePath;
    }

    if (imagePath.startsWith(environment.basePath)) {
      const protocol = window.location.protocol + '//';
      return `${protocol}${imagePath}`;
    }

    let cleanPath = imagePath.startsWith('/')
      ? imagePath.substring(1)
      : imagePath;

    if (!cleanPath.startsWith('chat/image/')) {
      cleanPath = `chat/image/${cleanPath}`;
    }

    const host = environment.basePath || window.location.host;
    const protocol = host.startsWith('http') ? '' : (window.location.protocol + '//');
    
    return `${protocol}${host}/${cleanPath}`;
  }

  async openAttachmentPreview(imageUrl: string): Promise<void> {
    const fileName = this.getFileName(imageUrl);
    const url = this.getImageUrl(imageUrl);
    
    if (!url) return;

    this.previewFileName = fileName;
    this.previewFilePath = imageUrl;
    this.isPreviewLoading = true;
    this.previewHasError = false;
    this.isLightboxOpen = true; 

    if (this.isImageFile(imageUrl)) {
      this.previewFileType = 'image';
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.isPreviewLoading = false;
    } else if (imageUrl.toLowerCase().endsWith('.pdf')) {
      this.previewFileType = 'pdf';
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(pdfBlob);
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${objectUrl}#toolbar=0&navpanes=0`);
        this.isPreviewLoading = false;
      } catch (error) {
        console.error('Failed to load PDF preview', error);
        this.previewHasError = true;
        this.isPreviewLoading = false;
      }
    } else {
      this.previewFileType = 'other';
      this.isPreviewLoading = false;
      this.previewHasError = true; 
    }
    
    this.cdr.detectChanges();
  }

  closePreview(): void {
    if (this.previewUrl && this.previewFileType === 'pdf') {
      const url = (this.previewUrl as any).changingThisBreaksApplicationSecurity;
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url.split('#')[0]);
      }
    }
    this.isLightboxOpen = false;
    this.previewUrl = null;
    this.previewFileName = '';
    this.previewFilePath = '';
    this.previewFileType = 'other';
    this.isPreviewLoading = false;
    this.previewHasError = false;
  }

  isImageFile(path: string | null | undefined): boolean {
    if (!path) return false;
    const extension = path.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension || '');
  }

  getFileIcon(path: string | null | undefined): string {
    if (!path) return 'bi-file-earmark';
    const extension = path.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'bi-file-earmark-pdf-fill';
      case 'doc':
      case 'docx':
        return 'bi-file-earmark-word-fill';
      case 'xls':
      case 'xlsx':
        return 'bi-file-earmark-excel-fill';
      case 'ppt':
      case 'pptx':
        return 'bi-file-earmark-slides-fill';
      case 'zip':
      case 'rar':
      case '7z':
        return 'bi-file-earmark-zip-fill';
      case 'txt':
        return 'bi-file-earmark-text-fill';
      default:
        return 'bi-file-earmark';
    }
  }

  getFileName(path: string | null | undefined): string {
    if (!path) return 'Attachment';
    const parts = path.split('/');
    const fileName = parts[parts.length - 1];
    return fileName;
  }

  async downloadFile(imageUrl: string, fileName: string): Promise<void> {
    const url = this.getImageUrl(imageUrl);
    if (!url) return;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed', error);
      window.open(url, '_blank');
    }
  }

  async videoCall() {
    this.selectedCallType = 'video';
    this.loadCallContacts();
  }

  audioCall() {
    this.selectedCallType = 'audio';
    this.loadCallContacts();
  }

  loadCallContacts(): void {
    this.isLoadingContacts = true;
    this.showContactSelector = true;
    this.callContacts = [];
    this.patientsService
      .getCallContacts(this.groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contacts) => {
          this.callContacts = contacts;
          this.isLoadingContacts = false;
        },
        error: (error) => {
          console.error('Failed to load call contacts', error);
          this.callContacts = [];
          this.isLoadingContacts = false;
        },
      });
  }

  initiateCall(contactId: string): void {
    if (!this.selectedCallType) return;
    const callType = this.selectedCallType;
    
    this.closeContactSelector();

    const modalRef = this.appModalService.openComponentModal(
      VideoCallModalComponent,
      {
        size: 'xl',
        centered: true,
        backdrop: 'static',
        keyboard: false,
      }
    );

    const comp = modalRef.componentInstance as VideoCallModalComponent;

    comp.calleeUserId = contactId;
    comp.callType = callType === 'video' ? 'Video' : 'Audio';
    comp.callContextType = 'Patient';
    comp.patientId = null;

    modalRef.closed.subscribe(async () => {});
  }

  closeContactSelector(): void {
    this.showContactSelector = false;
    this.selectedCallType = null;
    this.callContacts = [];
    this.contactSearchTerm = '';
  }

  toggleActionsMenu(event: Event) {
    event.stopPropagation();
    this.isActionsMenuOpen = !this.isActionsMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.actions-menu-container')) {
      this.isActionsMenuOpen = false;
    }
  }

  openConsultationModal(): void {
    if (!this.patientInfo) return;

    const modalRef = this.appModalService.openComponentModal(
      AskToSpecialistModalComponent,
      {
        size: 'lg',
        backdrop: true,
        keyboard: true,
        data: {
          patientId: this.groupId ?? '',
          patientName: this.patientInfo.name ?? '',
          patientSurname: this.patientInfo.surname ?? '',
        },
      },
    );

    modalRef.componentInstance.patientId = this.groupId ?? '';
    modalRef.componentInstance.patientName = this.patientInfo.name ?? '';
    modalRef.componentInstance.patientSurname = this.patientInfo.surname ?? '';
  }
}
